<template>
  <div class="shop-container">
    <div class="shop-header">
      <h2>商店</h2>
      <div class="player-info">
        <a-tag color="gold">金币: {{ gold }}</a-tag>
        <a-tag color="blue">等级: {{ playerLevel }}</a-tag>
        <a-tag color="purple">经验: {{ playerExp }}/{{ expToNextLevel }}</a-tag>
      </div>
    </div>
    
    <div class="shop-items">
      <h3>升级项目</h3>
      <div class="items-container">
        <div 
          v-for="item in shopItems" 
          :key="item.id"
          class="shop-item"
        >
          <a-card :title="item.name" class="item-card">
            <template #cover>
              <div class="item-icon">
                {{ item.icon }}
              </div>
            </template>
            <a-card-meta :description="item.description" />
            <div class="item-footer">
              <span class="price">价格: {{ item.price }} 金币</span>
              <a-button 
                type="primary" 
                size="small"
                :disabled="gold < item.price"
                @click="purchaseItem(item)"
              >
                购买
              </a-button>
            </div>
          </a-card>
        </div>
      </div>
    </div>
    
    <div class="player-upgrades">
      <h3>已购买的升级</h3>
      <div class="upgrades-container">
        <a-tag v-for="upgrade in playerUpgrades" :key="upgrade.id" color="blue">
          {{ upgrade.name }}
        </a-tag>
        <a-tag v-if="playerUpgrades.length === 0" color="default">暂无升级</a-tag>
      </div>
    </div>
    
    <div class="shop-actions">
      <a-button type="primary" @click="closeShop">关闭商店</a-button>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed } from 'vue';
import { useRouter } from 'vue-router';
import { Button, Card, Tag } from 'ant-design-vue';

export default {
  name: 'Shop',
  components: {
    AButton: Button,
    ACard: Card,
    ACardMeta: Card.Meta,
    ATag: Tag
  },
  props: {
    initialGold: {
      type: Number,
      default: 0
    },
    initialLevel: {
      type: Number,
      default: 1
    },
    initialExp: {
      type: Number,
      default: 0
    }
  },
  emits: ['close', 'purchase'],
  setup(props, { emit }) {
    const router = useRouter();
    
    // 玩家状态
    const gold = ref(props.initialGold);
    const playerLevel = ref(props.initialLevel);
    const playerExp = ref(props.initialExp);
    
    // 计算升级所需经验
    const expToNextLevel = computed(() => {
      return playerLevel.value * 100;
    });
    
    // 商店物品
    const shopItems = reactive([
      {
        id: 'health_upgrade',
        name: '生命值升级',
        icon: '❤️',
        description: '增加最大生命值20点',
        price: 50,
        effect: 'maxHp',
        value: 20
      },
      {
        id: 'speed_upgrade',
        name: '速度升级',
        icon: '⚡',
        description: '增加移动速度1点',
        price: 75,
        effect: 'speed',
        value: 1
      },
      {
        id: 'damage_upgrade',
        name: '伤害升级',
        icon: '⚔️',
        description: '增加子弹伤害5点',
        price: 100,
        effect: 'damage',
        value: 5
      },
      {
        id: 'fire_rate_upgrade',
        name: '射速升级',
        icon: '🔫',
        description: '增加攻击速度10%',
        price: 125,
        effect: 'fireRate',
        value: 0.1
      },
      {
        id: 'exp_boost',
        name: '经验加成',
        icon: '⭐',
        description: '获得经验增加20%',
        price: 150,
        effect: 'expBoost',
        value: 0.2
      },
      {
        id: 'gold_boost',
        name: '金币加成',
        icon: '💰',
        description: '获得金币增加20%',
        price: 200,
        effect: 'goldBoost',
        value: 0.2
      }
    ]);
    
    // 玩家已购买的升级
    const playerUpgrades = reactive([]);
    
    // 购买物品
    const purchaseItem = (item) => {
      if (gold.value >= item.price) {
        // 扣除金币
        gold.value -= item.price;
        
        // 添加到已购买升级
        playerUpgrades.push({
          id: item.id,
          name: item.name
        });
        
        // 发送购买事件
        emit('purchase', item);
        
        // 显示购买成功消息
        alert(`成功购买 ${item.name}!`);
      } else {
        alert('金币不足!');
      }
    };
    
    // 关闭商店
    const closeShop = () => {
      emit('close', {
        gold: gold.value,
        level: playerLevel.value,
        exp: playerExp.value
      });
    };
    
    return {
      gold,
      playerLevel,
      playerExp,
      expToNextLevel,
      shopItems,
      playerUpgrades,
      purchaseItem,
      closeShop
    };
  }
};
</script>

<style scoped>
.shop-container {
  display: flex;
  flex-direction: column;
  padding: 20px;
  max-width: 1000px;
  margin: 0 auto;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.shop-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 10px;
}

.shop-header h2 {
  margin: 0;
}

.player-info {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.shop-items h3 {
  margin-top: 0;
}

.items-container {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 30px;
}

.shop-item {
  flex: 1 1 calc(33.333% - 20px);
  min-width: 200px;
}

.item-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.item-icon {
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  background-color: #f5f5f5;
}

.item-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 15px;
}

.price {
  font-weight: bold;
  color: #faad14;
}

.player-upgrades h3 {
  margin-top: 0;
}

.upgrades-container {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 30px;
  min-height: 50px;
}

.shop-actions {
  display: flex;
  justify-content: flex-end;
}
</style>